// ============================================
// StatusBadge Component
// Badge for status indicators (IN STOCK, LOW STOCK, etc.)
// ============================================

import { cn } from '../../utils/cn';

// ============================================
// Types
// ============================================

type StatusVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

interface StatusBadgeProps {
  /** Status variant determines colors */
  variant: StatusVariant;
  /** Display text */
  children: React.ReactNode;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Additional className */
  className?: string;
}

// ============================================
// Styles
// ============================================

const variantStyles: Record<StatusVariant, string> = {
  success: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  warning: 'bg-amber-50 text-amber-600 border-amber-200',
  danger: 'bg-red-50 text-red-600 border-red-200',
  info: 'bg-blue-50 text-primary-600 border-blue-200',
  neutral: 'bg-slate-100 text-slate-600 border-slate-200',
};

const sizeStyles: Record<'sm' | 'md', string> = {
  sm: 'px-2 py-0.5 text-[10px]',
  md: 'px-2.5 py-1 text-xs',
};

// ============================================
// Component
// ============================================

export function StatusBadge({
  variant,
  children,
  size = 'sm',
  className,
}: StatusBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md border font-bold uppercase',
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
    >
      {children}
    </span>
  );
}

// ============================================
// Stock Status Helper
// Maps stock status strings to variants
// ============================================

export type StockStatusType = 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK';

const stockStatusConfig: Record<
  StockStatusType,
  { variant: StatusVariant; label: string }
> = {
  IN_STOCK: { variant: 'success', label: 'IN STOCK' },
  LOW_STOCK: { variant: 'warning', label: 'LOW STOCK' },
  OUT_OF_STOCK: { variant: 'danger', label: 'OUT STOCK' },
};

interface StockStatusBadgeProps {
  status: StockStatusType;
  className?: string;
}

export function StockStatusBadge({ status, className }: StockStatusBadgeProps) {
  const config = stockStatusConfig[status];
  return (
    <StatusBadge variant={config.variant} className={className}>
      {config.label}
    </StatusBadge>
  );
}
